<template>
  <div
    v-if="isBackgroundImage"
    :class="$b({ bg: isBackgroundImage, rounded })"
  >
    <img
      :class="$b('image')"
      :loading="lazyLoad ? 'lazy' : 'eager'"
      decoding="async"
      :srcset="srcset"
      :src="baseSrc"
      :sizes="srcSizes"
      :alt="image.alt"
      :width="baseWidth"
      :height="baseHeight"
      :style="styles"
    />

    <slot />
  </div>
  <img
    v-else
    :class="$b({ rounded })"
    :loading="lazyLoad ? 'lazy' : 'eager'"
    decoding="async"
    :srcset="srcset"
    :src="baseSrc"
    :sizes="srcSizes"
    :alt="image.alt"
    :width="baseWidth"
    :height="baseHeight"
    :style="styles"
  />
</template>

<script>
import aspectRatio from '~/assets/js/constants/aspectRatio'
import { resolveUrlParams } from '~/assets/js/helpers/url'

export default {
  name: 'DynamicImage',

  props: {
    image: {
      type: Object,
      required: true,
    },

    rounded: {
      type: Boolean,
      default: true,
    },

    aspectRatio: {
      type: String,
      default: 'auto',
      validator: (value) => aspectRatio.includes(value),
    },

    transformWidths: {
      type: Array,
      default: () => [800, 1200, 1600, 2000, 3000],
    },

    sizes: {
      type: Array,
      default: () => [
        { minWidth: 1500, width: 750 },
        { minWidth: 1400, width: 600 },
        { minWidth: 1000, width: 500 },
        { minWidth: 700, width: 400 },
        { width: 350 },
      ],
    },

    lazyLoad: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    crops() {
      const params = {
        auto: 'format',
        cs: 'srgb',
        fit: 'crop',
      }

      if (this.aspectRatio !== 'auto') params.ar = this.aspectRatio

      if (
        this.image.focalPoint &&
        this.image.focalPoint.x &&
        this.image.focalPoint.y
      ) {
        const { x, y } = this.image.focalPoint
        params['fp-x'] = x
        params['fp-y'] = y
      }

      return this.transformWidths.map((width) => {
        const src = this.image.src ?
          resolveUrlParams(this.image.src, { ...params, w: width })
          : this.image?.getSrc({ ...params, width, height: width })
        
        return {
          src,
          width,
        }
      })
    },

    isBackgroundImage() {
      return !!this.$slots.default
    },

    srcset() {
      return this.crops.map((crop) => `${crop.src} ${crop.width}w`).join(', ')
    },

    srcSizes() {
      return this.sizes
        .map((size) =>
          size.minWidth
            ? `(min-width: ${size.minWidth}px) ${size.width}px`
            : `${size.width}px`
        )
        .join(', ')
    },

    baseSrc() {
      return this.crops[0].src
    },

    baseWidth() {
      return this.crops[0].width
    },

    baseHeight() {
      if (!this.actualAspectRatio) return false

      const parts = this.actualAspectRatio.split('/').map((i) => parseInt(i))
      return (this.baseWidth / (parts[0] / parts[1])).toFixed(0)
    },

    actualAspectRatio() {
      if (!this.image.width || !this.image.height) return false

      return this.aspectRatio === 'auto'
        ? `${this.image.width}/${this.image.height}`
        : this.aspectRatio.replace(':', '/')
    },

    getAspectRatio() {
      if (!this.actualAspectRatio) return false

      return `aspect-ratio: ${this.actualAspectRatio};`
    },

    getFocalPoint() {
      if (!this.image.focalPoint) return

      const xPoint = `${this.image.focalPoint.x * 100}%`
      const yPoint = `${this.image.focalPoint.y * 100}%`

      return `object-position: ${xPoint} ${yPoint};`
    },

    styles() {
      return `${this.getAspectRatio} ${this.getFocalPoint}`
    },
  },
}
</script>

<style lang="postcss">
.DynamicImage {
  width: 100%;
  height: auto;
  content-visibility: auto;

  &--bg {
    @apply relative;

    .DynamicImage__image {
      @apply w-full h-full
        absolute top-0 left-0
        object-cover;
    }
  }
}

.DynamicImage--rounded {
  border-radius: 10px;
}
</style>
